import { useQuery } from "@tanstack/react-query";
import { useMemo, useRef, useEffect } from "react";
import { supabase } from "@/integrations/supabase/client";
import { LoadFilters, LoadListItem, InstallerProfile, InstallationsMap, LoadInstallation } from "@/types/loads";
import { filterLoads, hasActiveFilters } from "@/lib/load-helpers";
import { queryKeys } from "@/lib/query-keys";

export function useLoadsQuery(filters: LoadFilters) {
  // Fetch installers for filter dropdown
  const { data: installers } = useQuery({
    queryKey: queryKeys.users.installers(),
    queryFn: async () => {
      const { data, error } = await supabase
        .from("profiles")
        .select("id, first_name, last_name")
        .is("deleted_at", null);
      if (error) throw error;
      return data as InstallerProfile[];
    },
  });

  // Fetch loads with installer info - filters included in queryKey for reactivity
  const { data: loads, isLoading } = useQuery({
    queryKey: [...queryKeys.loads.all, filters.date?.toISOString(), filters.status, filters.installerId],
    queryFn: async () => {
      const { data, error } = await supabase
        .from("installation_loads")
        .select(`
          *,
          installer:profiles!installation_loads_installer_id_fkey(id, first_name, last_name)
        `)
        .is("deleted_at", null)
        .order("load_date", { ascending: false });

      if (error) throw error;
      return data as LoadListItem[];
    },
  });

  // Stabiloi load ID:t - estää turhat uudelleenkyselyt
  const loadIds = useMemo(() => 
    loads?.map(l => l.id).sort().join(',') || '', 
    [loads]
  );
  
  // Käytä ref:iä edelliseen loadIds:iin vertailuun
  const prevLoadIdsRef = useRef(loadIds);
  useEffect(() => {
    prevLoadIdsRef.current = loadIds;
  }, [loadIds]);

  // Fetch installations for each load - vakaa queryKey
  const { data: installationsMap } = useQuery({
    queryKey: [...queryKeys.loads.installations(), loadIds],
    queryFn: async () => {
      if (!loads || loads.length === 0) return {};
      
      const { data, error } = await supabase
        .from("installations")
        .select(`
          *,
          order:orders(
            order_number,
            order_type,
            customer:customers(first_name, last_name, company_name),
            order_lines(
              id,
              description,
              quantity,
              unit_price,
              product:products(name, sku),
              configuration:configurations(
                id,
                product_model:product_models(name),
                config_components(
                  id,
                  quantity,
                  width,
                  height,
                  thickness,
                  source_model_name,
                  material:materials(name),
                  model_component:model_components(
                    position,
                    component_type:component_types(name, label)
                  )
                )
              )
            )
          ),
          cemetery:cemeteries(name, city, address, latitude, longitude)
        `)
        .in("load_id", loads.map(l => l.id))
        .is("deleted_at", null)
        .order("load_position", { ascending: true });

      if (error) throw error;

      // Group by load_id with proper typing
      const map: InstallationsMap = {};
      (data as LoadInstallation[] | null)?.forEach((inst) => {
        if (inst.load_id) {
          if (!map[inst.load_id]) map[inst.load_id] = [];
          map[inst.load_id].push(inst);
        }
      });
      return map;
    },
    enabled: !!loads && loads.length > 0,
  });

  // Filter loads client-side using helper
  const filteredLoads = useMemo(() => {
    if (!loads) return [];
    return filterLoads(loads, filters);
  }, [loads, filters]);

  // Check active filters using helper
  const activeFilters = hasActiveFilters(filters);

  return {
    loads: loads || [],
    filteredLoads,
    installers: installers || [],
    installationsMap: installationsMap || {},
    isLoading,
    hasActiveFilters: activeFilters,
  };
}
